import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { useParams } from "react-router";

// Actions
import {
  closeDownloadCampaignReportForm,
  downloadCampaignPdfReport,
  generateMavinReport,
  getCampaignOverview,
} from "../../../actions/campaign-report/CampaignReportActions";
import {
  removeCityFromCampaign,
  updateCampaignTitle,
} from "../../../actions/campaign/CampaignActions";

// Custom Hooks
import { useCampaignPdfReportDownload } from "../../../hooks/ReportsDownloadHooks";

// Constants and Utils
import {
  CampaignReportStatus,
  UrlKeys,
} from "../../../constants/GeneralConstants";
import { useUrlKeyCheck } from "../../../mavin/utils/hooks/HooksUtil";
import { checkIsCustomTgPresent } from "../../../utils/CampaignCustomTgUtils";

// Components
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import Spinner from "../../../components/spinner/Spinner";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import TitleEditor from "../../../components/title-editor/TitleEditor";
import CampaignViewTable from "./CampaignViewTable";
import ConfirmationForm from "../../../components/confirmation-form/ConfirmationForm";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";
import DownloadReportsForm from "../../../components/download-reports-from/DownloadReportsForm";
import CampaignTgViewButton from "../../../components/campaign-tg-form/CampaignTgViewButton";

// Urls
import { RedirectTo } from "../../../urls/PageLinksURL";

// Pages and Sections
import OverviewSection from "./OverviewSection";
import ReportSection from "./ReportSection";

function PageHeaderSection({ campaign, loader, isMantaraySpecific }) {
  const dispatch = useDispatch();

  const {
    id: campaignId,
    // targetGroupId,
    // targetGroupName = "",
  } = campaign || {};

  // tg name
  // const tgName = targetGroupId ? targetGroupName : "NA";

  // variables
  const pageTitle = (
    <div className="d-flex align-items-center">
      <TitleEditor
        title={campaign.title}
        onTitleChange={(titleStr) =>
          dispatch(updateCampaignTitle(titleStr, campaignId))
        }
        loader={loader}
        inputBoxClass={"rounded-lg"}
        titleClass={"px-0 mb-0 h1"}
      />
      {/* <h3 className="mb-0"> | TargetGroup: {tgName}</h3> */}
    </div>
  );

  const pageActions = (
    <div className="d-flex align-items-center">
      <CampaignTgViewButton selectedTgData={campaign} />

      <ReportSection
        campaign={campaign}
        isMantaraySpecific={isMantaraySpecific}
      />
    </div>
  );

  return <PageHeader title={pageTitle} actions={pageActions} />;
}

function UpdateMavinStatsIndicator() {
  const campaignDurationChanging = useSelector(
    (state) => state.campaign.campaignDurationChanging
  );

  // Checks for campaignDurationChanging
  if (!campaignDurationChanging) {
    return null;
  }

  return (
    <AlertMessage
      className="mb-1 py-1"
      spinnerClassName="spinner-border-sm"
      textMessage="Updating Metrics"
    />
  );
}

/**
 * Page
 */
function CampaignViewPage() {
  const dispatch = useDispatch();
  const { id } = useParams();

  //Selector
  const campaign = useSelector(
    (state) => state.campaignReport.campaignSummary.campaign
  );

  const { title } = campaign || {};
  const summary = useSelector(
    (state) => state.campaignReport.campaignSummary.summary
  );

  const loading = useSelector(
    (state) => state.campaignReport.campaignSummaryLoading
  );

  // Title Loading
  const titleLoader = useSelector((state) => state.campaign.titleUpdateLoader);

  // Confirmation Form :: [true/false and dataObject]
  const openConfirmationForm = useSelector(
    (state) => state.confirmationModal.openModal
  );
  const processDataObject = useSelector(
    (state) => state.confirmationModal.processDataObject
  );

  // Download Campaign Report Modal
  const openDownloadCampaignReportModal = useSelector(
    (state) => state.campaignReport.openDownloadCampaignReportModal
  );

  // Link to pass in CampaignSidebar
  const redirectLinkObj = {
    campaignOverviewLink: RedirectTo.campaignViewPageUrl.replace(
      ":campaignId",
      id
    ),
    citySummaryLink: RedirectTo.campaignCityViewPageUrl.replace(
      ":campaignId",
      id
    ),
  };

  // Dispatch
  useEffect(() => {
    dispatch(getCampaignOverview(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (
      !loading &&
      summary &&
      summary.reportStatus !== CampaignReportStatus.GENERATED
    ) {
      dispatch(generateMavinReport(id));
    }
  }, [summary?.reportStatus]);

  // pdf report download effects
  useCampaignPdfReportDownload(title);

  const { mantaraySpecific } = UrlKeys;
  const isMantaraySpecific = useUrlKeyCheck(mantaraySpecific);

  //Checks for page loading
  if (loading) {
    return <Spinner className="spinner-center mt-2" />;
  }
  //Checks for campaign and summary
  if (!campaign || !summary) {
    return null;
  }

  const { infoByCity, targetGroupId } = campaign || {};

  // custom tg
  const isCustomTgSelected = checkIsCustomTgPresent(campaign);

  // Functions to "remove city from existing campaign"
  function removeCity() {
    dispatch(removeCityFromCampaign(id, processDataObject.cityId));
  }

  function handleDownloadReportSubmit(isSentViaEmail, emailIds) {
    dispatch(downloadCampaignPdfReport(id, isSentViaEmail, emailIds));
  }

  return (
    <>
      {/* Sidebar */}
      <CampaignSidebar campaign={campaign} redirectLinkObj={redirectLinkObj} />

      <div className="content-wrapper" id="cont-campaign-details">
        {/* Page Header */}
        <PageHeaderSection
          campaign={campaign}
          loader={titleLoader}
          isMantaraySpecific={isMantaraySpecific}
        />

        <div className="page-content">
          {/* Campaign Performance and Stats Card */}
          <OverviewSection
            campaign={campaign}
            summary={summary}
            showExcelReportAlert={true}
          />

          <div className="mt-5 d-flex">
            <h4 className="mr-2">{"City highlights"}</h4>
            {/* Mavin Stats Indicator */}
            <UpdateMavinStatsIndicator />
          </div>

          {/* Table */}
          <CampaignViewTable
            infoByCity={infoByCity}
            targetGroupId={targetGroupId}
            isCustomTgSelected={isCustomTgSelected}
          />
        </div>
      </div>

      {/** Modals */}
      {openConfirmationForm && <ConfirmationForm onClick={removeCity} />}

      {/* Download Campaign Report */}
      {openDownloadCampaignReportModal && (
        <DownloadReportsForm
          handleSubmit={handleDownloadReportSubmit}
          handleClose={() => dispatch(closeDownloadCampaignReportForm())}
        />
      )}
    </>
  );
}

export default CampaignViewPage;
